/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from "moment-timezone";

/**
 * @param {number} fechaNumero Timestamp con la fecha
 * @param {boolean} devolverHora Si devolver la fecha o también la hora (true)
 * @param {string} separador Separador de fecha si / ó -
 * @return {string} La fecha en dd/mm/aaaa con hora si devolverHora es true
 */
export function utilFechaConvertirNumeroString(
  fechaNumero: number,
  devolverHora: boolean,
  separador: string
): string {
  if (fechaNumero != null) {
    const fecha = new Date(fechaNumero);

    const diaNumber = fecha.getDate();
    let dia = "";
    if (diaNumber < 10) {
      dia = "0" + diaNumber;
    } else {
      dia = diaNumber.toString();
    }

    const mesNumber = fecha.getMonth() + 1;
    let mes = "";
    if (mesNumber < 10) {
      mes = "0" + mesNumber;
    } else {
      mes = mesNumber.toString();
    }

    const horaNumber = fecha.getHours();
    let hora = "";
    if (horaNumber < 10) {
      hora = "0" + horaNumber;
    } else {
      hora = horaNumber.toString();
    }

    const minutoNumber = fecha.getMinutes();
    let minuto = "";
    if (minutoNumber < 10) {
      minuto = "0" + minutoNumber;
    } else {
      minuto = minutoNumber.toString();
    }
    if (devolverHora) {
      // eslint-disable-next-line max-len
      return `${dia}${separador}${mes}${separador}${fecha.getFullYear()} ${hora}:${minuto}`;
    } else {
      return `${dia}${separador}${mes}${separador}${fecha.getFullYear()}`;
    }
  } else {
    return "";
  }
}

/**
 * @param {number} fechaNumero Timestamp con la fecha
 * @return {string} La fecha en aaaa-mm-dd con hora si devolverHora es true
 */
export function utilFechaConvertirNumeroAAAAMMDD(fechaNumero: number): string {
  if (fechaNumero != null) {
    const fecha = new Date(fechaNumero);

    const diaNumber = fecha.getDate();
    let dia = "";
    if (diaNumber < 10) {
      dia = "0" + diaNumber;
    } else {
      dia = diaNumber.toString();
    }

    const mesNumber = fecha.getMonth() + 1;
    let mes = "";
    if (mesNumber < 10) {
      mes = "0" + mesNumber;
    } else {
      mes = mesNumber.toString();
    }

    return `${fecha.getFullYear()}-${mes}-${dia}`;
  } else {
    return "";
  }
}

/**
 * @param {number} year Como number
 * @param {number} month Como number
 * @param {number} day Como number
 * @return {number} Timestamp con zona horaria Montevideo
 */
export function utilFechaGetTimestampFromYearMonthDay(
  year: number,
  month: number,
  day: number
): number {
  const montevideoTime = moment.tz(
    [year, month - 1, day],
    "America/Montevideo"
  );

  // Return the Unix timestamp
  return montevideoTime.valueOf();
}

/**
 * @param {number} year Como number
 * @param {number} month Como number
 * @param {number} day Como number
 * @param {number} hour Como number
 * @param {number} minute Como number
 * @param {number} second Como number
 * @return {number} Timestamp con zona horaria Montevideo
 */
export function utilFechaGetTimestamp(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
): number {
  const montevideoTime = moment.tz(
    [year, month - 1, day, hour, minute, second],
    "America/Montevideo"
  );

  // Return the Unix timestamp
  return montevideoTime.valueOf();
}

/**
 * @param {string} dateStr En formato aaaa-mm-dd
 * @return {number} Timestamp con zona horaria Montevideo
 */
export function utilFechaGetTimestampFromDate(dateStr: string): number {
  // Split the date string into components
  const parts = dateStr.split("-");

  // Parse each part into a number
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  if (day > 31) {
    throw new Error(
      // eslint-disable-next-line max-len
      "Error llamando a utilFechaGetTimestampFromDate, se espera formato de fecha aaaa-mm-dd pero se recibio otro. No habrías querido llamar a utilFechaGetTimestampFromDDMMAAAA ?"
    );
  }

  // Return an object with the separated values
  return utilFechaGetTimestampFromYearMonthDay(year, month, day);
}

/**
 * @param {string} dateStr En formato dd/mm/aaaa
 * @return {number} Timestamp con zona horaria Montevideo
 */
export function utilFechaGetTimestampFromDDMMAAAA(dateStr: string): number {
  // Split the date string into components
  const parts = dateStr.split("/");

  // Parse each part into a number
  const year = parseInt(parts[2], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[0], 10);

  // Return an object with the separated values
  return utilFechaGetTimestampFromYearMonthDay(year, month, day);
}

/**
 * @return {number} Timestamp del tiempo actual con zona horaria Montevideo
 */
export function utilFechaGetCurrentTime(): number {
  const timeZone = "America/Montevideo";
  const nowInMontevideo = moment().tz(timeZone);
  return nowInMontevideo.valueOf();
}

/**
 * @return {string} Date in yyyy-mm format from timestamp
 */
export function utilFechaGetYYYYMM(date: number): string {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  return `${year}-${month}`;
}

/**
 * @return {number} Timestamp del tiempo actual con zona horaria Montevideo
 */
export function utilFechaGetCurrentTimeCustom(): number {
  const timeZone = "America/Montevideo";
  const date = new Date();

  // Use Intl.DateTimeFormat to get the timezone offset and adjust the time
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3
  });

  const parts = formatter.formatToParts(date);
  const formattedDateString = `${parts.find(p => p.type === "year")?.value}-${parts.find(p => p.type === "month")?.value}-${parts.find(p => p.type === "day")?.value}T${parts.find(p => p.type === "hour")?.value}:${parts.find(p => p.type === "minute")?.value}:${parts.find(p => p.type === "second")?.value}.${parts.find(p => p.type === "fractionalSecond")?.value}Z`;

  const utcDate = new Date(formattedDateString);
  return utcDate.getTime();
}

/**
 * @return {string} El dia actual en formato dd/mm/aaaa
 */
export function utilFechaGetDiaActualDDMMYYYY(): string {
  const date = new Date();
  const day = (date.getDate().toString() as any).padStart(2, "0");
  const month = ((date.getMonth() + 1).toString() as any).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * @return {string} El dia actual en formato aaaa-mm-dd
 */
export function utilFechaGetDiaActualYYYYMMDD(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = ((date.getMonth() + 1).toString() as any).padStart(2, "0");
  const day = (date.getDate().toString() as any).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
